import React, { useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import API_BASE_URL from "../../api/config";
import COLLEGE_MAP from "../../constants/colleges";

// --- ICONS and UI Components (No changes needed here) ---
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
    />
  </svg>
);
const EmptyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 15.75l-2.489-2.489m0 0a3.375 3.375 0 10-4.773-4.773 3.375 3.375 0 004.774 4.774zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);
const LoadingSpinner = ({ message }) => (
  <div className="loading-overlay">
    <div className="spinner"></div>
    <p>{message}</p>
  </div>
);
const EmptyState = ({ message, itemType }) => (
  <div className="empty-state">
    <EmptyIcon />
    <h3>{message}</h3>
    <p>There are currently no {itemType} to display.</p>
  </div>
);

const API_ENDPOINTS = {
  getStudents: `${API_BASE_URL}/api/profile/read`,
  getStudentDetails: `${API_BASE_URL}/api/profile/read`,
  getApprovedFacilitators: `${API_BASE_URL}/api/Facilitator_Panel/read`,
  allocateMentor: `${API_BASE_URL}/api/Facilitator_Panel/allocate_mentor`,
  checkAreaLeaderStatus: `${API_BASE_URL}/api/Facilitator_Panel/read`,
};

const StudentAllocation = () => {
  // --- State Management ---
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [searchText, setSearchText] = useState("");

  // --- Loading & Authorization States ---
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isSaving, setIsSaving] = useState({});
  const [areaOfAreaLeader, setAreaofAreaLeader] = useState(null);

  const checkAuthorizationAndFetchData = useCallback(async () => {
    setIsLoading(true);
    const userRole = Cookies.get("role");
    const userEmail = Cookies.get("email");
    let authorized = false;

    if (userRole === "admin") {
      authorized = true;
    } else if (userRole === "area_leader") {
      if (!userEmail) {
        Swal.fire({
          icon: "error",
          title: "Session Error",
          text: "Your email could not be found. Please log in again.",
        }).then(() => window.history.back());
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetch(API_ENDPOINTS.checkAreaLeaderStatus);
        const leaders = await res.json();
        const currentUser = Array.isArray(leaders)
          ? leaders.find(
              (l) =>
                (l.Email || l.email) === userEmail && l.role_approved === "1",
            )
          : null;
        if (currentUser) {
          authorized = true;
          setAreaofAreaLeader(
            COLLEGE_MAP[currentUser.Area] || currentUser.Area,
          );
        } else {
          Swal.fire({
            icon: "warning",
            title: "Account Not Approved",
            text: "Your account is pending approval from an administrator. You cannot allocate students yet.",
            confirmButtonText: "Go Back",
          }).then(() => window.history.back());
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Authorization Failed",
          text: "Could not verify your account status.",
        }).then(() => window.history.back());
      }
    } else {
      Swal.fire({
        icon: "error",
        title: "Unauthorized Access",
        text: "You do not have permission to view this page.",
      }).then(() => window.history.back());
    }

    if (!authorized) {
      setIsLoading(false);
      return;
    }

    setIsAuthorized(true);

    try {
      const [studentRes, facilitatorRes] = await Promise.all([
        fetch(API_ENDPOINTS.getStudents),
        fetch(API_ENDPOINTS.getApprovedFacilitators),
      ]);

      if (!studentRes.ok || !facilitatorRes.ok)
        throw new Error("Failed to fetch initial data.");

      const studentData = await studentRes.json();
      const facilitatorData = await facilitatorRes.json();

      const normalizedStudents = Array.isArray(studentData)
        ? studentData.map((s) => ({
            id: s.user_id,
            name: s.full_name,
            email: s.email,
            phone_number: s.phone_number || "N/A",
            department: s.department || "N/A",
            college: COLLEGE_MAP[s.college] || s.college || "N/A",
            allocatedMentor: s.Mentors_Name || "N/A",
            profile_pic: s.profile_image
              ? `https://ethiccraft.org/${s.profile_image}`
              : null,
            Chanting: s.Chanting || "N/A",
            sincerity: s.sincerity || "N/A",
            gender: s.gender || "N/A",
            dob: s.dob || "N/A",
            blood_group: s.blood_group || "N/A",
            bio: s.bio || "N/A",
            year_of_study: s.year_of_study || "N/A",
            skills: s.skills || "N/A",
            address: s.address || "N/A",
            social_links: s.social_links || "N/A",
          }))
        : [];

      const unallocated = normalizedStudents
        .filter((s) => s.allocatedMentor === "N/A")
        .sort((a, b) => a.name.localeCompare(b.name));
      const allocated = normalizedStudents
        .filter((s) => s.allocatedMentor !== "N/A")
        .sort((a, b) => a.name.localeCompare(b.name));
      const sortedStudents = [...unallocated, ...allocated];

      setStudents(sortedStudents);
      setFilteredStudents(sortedStudents);

      const initialAllocs = {};
      normalizedStudents.forEach((s) => {
        initialAllocs[s.id] = s.allocatedMentor;
      });
      setAllocations(initialAllocs);
      const normalizedFacilitators = Array.isArray(facilitatorData)
        ? facilitatorData
            .filter(
              (f) =>
                f.role === "facilitator" && // ✅ ONLY facilitators
                f.role_approved === "1", // ✅ ONLY approved ones
            )
            .map((f) => ({
              name: f.Full_Name || f.username,
              email: f.Email || f.email,
              area: COLLEGE_MAP[f.Area] || f.Area || "N/A",
            }))
        : [];

      const visibleFacilitators =
        Cookies.get("role") === "area_leader"
          ? normalizedFacilitators.filter((f) => f.area === areaOfAreaLeader)
          : normalizedFacilitators;

      setFacilitators(visibleFacilitators);
    } catch (error) {
      console.error("Data Fetch Error:", error);
      Swal.fire({
        icon: "error",
        title: "Data Load Failed",
        text: "Could not load student or facilitator data.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [areaOfAreaLeader]);

  useEffect(() => {
    checkAuthorizationAndFetchData();
  }, [checkAuthorizationAndFetchData]);

  useEffect(() => {
    const lowerCaseSearch = searchText.toLowerCase();
    const filtered = students.filter(
      (student) =>
        student.name.toLowerCase().includes(lowerCaseSearch) ||
        student.email.toLowerCase().includes(lowerCaseSearch),
    );
    setFilteredStudents(filtered);
  }, [searchText, students]);

  const handleAllocationChange = (studentId, facilitatorEmail) => {
    setAllocations((prev) => ({ ...prev, [studentId]: facilitatorEmail }));
  };

  const handleViewDetails = async (student) => {
    Swal.fire({
      html: `<p>Fetching details...</p>`,
      showCloseButton: true,
      showConfirmButton: false,
      width: "650px",
      heightAuto: false,
      backdrop: true,
      allowOutsideClick: true,
      customClass: {
        popup: "student-popup",
        htmlContainer: "student-scroll",
      },
      willClose: () => {
        document.body.style.overflow = "";
      },
      didOpen: async () => {
        document.body.style.overflow = "hidden";
        const detailsHTML = `
                    <div class="student-details-modal">
                        <img src="${student.profile_pic || "default_avatar.png"}" class="student-avatar">

                        <h3 class="student-name">${student.name}</h3>

                        <div class="info-section">
                            <p><strong>Email:</strong> ${student.email || "N/A"}</p>
                            <p><strong>Phone:</strong> ${student.phone_number || "N/A"}</p>
                            <p><strong>Mentor:</strong> ${allocations[student.id] || "Not Assigned"}</p>
                        </div>

                        <div class="info-section">
                            <p><strong>DOB:</strong> ${student.dob || "N/A"}</p>
                            <p><strong>Gender:</strong> ${student.gender || "N/A"}</p>
                            <p><strong>Blood Group:</strong> ${student.blood_group || "N/A"}</p>
                        </div>

                        <div class="info-section">
                            <p><strong>College:</strong> ${student.college || "N/A"}</p>
                            <p><strong>Department:</strong> ${student.department || "N/A"}</p>
                            <p><strong>Year:</strong> ${student.year_of_study || "N/A"}</p>
                        </div>

                        <div class="info-section">
                            <p><strong>Bio:</strong> ${student.bio || "N/A"}</p>
                            <p><strong>Skills:</strong> ${student.skills || "N/A"}</p>
                            <p><strong>Address:</strong> ${student.address || "N/A"}</p>
                        </div>

                        <div class="info-section highlight">
                            <p><strong>Chanting Rounds:</strong> ${student.Chanting || "N/A"}</p>
                            <p><strong>Sincerity:</strong> ${student.sincerity || "N/A"}</p>
                        </div>
                    </div>
                `;

        Swal.update({ html: detailsHTML });
      },
    });
  };

  const handleSaveAllocation = async (studentId) => {
    setIsSaving((prev) => ({ ...prev, [studentId]: true }));
    const facilitator_Name = allocations[studentId];

    if (!facilitator_Name) {
      Swal.fire({
        icon: "warning",
        title: "Mentor Required",
        text: "Please select a facilitator before saving.",
      });
      return;
    }

    try {
      const res = await fetch(API_ENDPOINTS.allocateMentor, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_id: studentId,
          mentor_name: facilitator_Name,
        }),
      });
      const result = await res.json();

      if (result.success) {
        Swal.fire({
          toast: true,
          icon: "success",
          title: "Allocation Saved!",
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });

        // ✅ IMPROVEMENT: Update both student lists to keep UI in sync
        const updateState = (prev) =>
          prev.map((s) =>
            s.id === studentId
              ? { ...s, allocatedMentor: facilitator_Name }
              : s,
          );
        setStudents(updateState);
        setFilteredStudents(updateState);
      } else {
        throw new Error(result.message || "Failed to save allocation.");
      }
    } catch (error) {
      Swal.fire({ icon: "error", title: "Save Failed", text: error.message });
    } finally {
      setIsSaving((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  // --- Render Logic (No changes needed here) ---
  if (isLoading) {
    return <LoadingSpinner message="Verifying access and loading data..." />;
  }

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="main">
      <div className="student-allocation-container">
        <div className="container-header">
          <h1 className="header-title">Student Allocation</h1>
          <div className="allocate-search-box">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search students by name or email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {filteredStudents.length > 0 ? (
            <table className="allocation-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Details</th>
                  <th>Allocate Mentor</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents
                  .filter(
                    (student) =>
                      !areaOfAreaLeader || student.college === areaOfAreaLeader,
                  )
                  .map((student) => {
                    return (
                      <tr
                        key={student.id}
                        className={
                          allocations[student.id] === "N/A"
                            ? "unallocated-row"
                            : ""
                        }
                      >
                        <td>
                          <div className="student-info">
                            <span className="student-name">{student.name}</span>
                            <span className="student-email">
                              {student.email}
                            </span>
                          </div>
                        </td>
                        <td>
                          <button
                            className="btn btn-view"
                            onClick={() => handleViewDetails(student)}
                          >
                            View
                          </button>
                        </td>
                        <td>
                          <select
                            className="mentor-select"
                            value={allocations[student.id] || "N/A"}
                            onChange={(e) =>
                              handleAllocationChange(student.id, e.target.value)
                            }
                          >
                            <option value="N/A">N/A</option>
                            {facilitators.map((fac) => (
                              <option key={fac.email} value={fac.name}>
                                {`${fac.name} - ${fac.email} - ${fac.area}`}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                            className="btn btn-save"
                            onClick={() => handleSaveAllocation(student.id)}
                            disabled={
                              isSaving[student.id] ||
                              allocations[student.id] ===
                                student.allocatedMentor
                            }
                          >
                            {isSaving[student.id] ? "Saving..." : "Save"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          ) : (
            <EmptyState message="No Students Found" itemType="students" />
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentAllocation;
